// Transport layer (axios instances + interceptors) lives in ./axiosConfig.js
import api, { githubApi } from './axiosConfig';

// ============================================
// Request body naming — two families, not one convention
// ============================================
// The incident API is not one naming convention. It is two, split by endpoint,
// and each endpoint matches its own documentation:
//
//   POST  .../review     camelCase  both ways   rcaSummary, reviewedBy
//   POST  .../escalate   snake_case both ways   triggered_by
//   PATCH .../assign     snake_case both ways   assigned_by
//   PATCH .../status     snake_case both ways   changed_by
//
// This app sent snake_case everywhere, so every post-incident review it ever
// submitted was dropped before reaching the domain logic — the key ignored, then
// reported as missing. Verified against sitops:
//
//   POST .../review  {"rca_summary": "x"}  → 400 {"rcaSummary": "RCA summary is required"}
//   POST .../review  {"rcaSummary":  "x"}  → 400 {"reviewedBy": "reviewedBy is required"}
//
// That is TSB-4219. Escalate, assign and status were NOT affected by naming --
// their snake_case has always been correct. An earlier version of this comment
// said otherwise, reasoning from the camelCase keys in those endpoints'
// validation errors; those are Bean Validation reporting Java field names, not
// JSON property names, and a field named triggeredBy bound from triggered_by
// reports exactly that way. Corrected by the backend team.
//
// Each key goes out under BOTH spellings rather than a per-endpoint table.
// Unknown properties are ignored by this API — the snake_case probe above was
// not rejected as unrecognised, only reported as missing — so the duplicate
// costs nothing, and one rule beats a table that has already been got wrong
// once. The backend has since made requests accept both spellings, which makes
// this redundant rather than wrong; it can go once that reaches every
// environment.
//
// One-word keys (`status`, `assignee`, `reason`) are identical either way and
// are left alone.
//
// This belongs here rather than at the call sites: the naming of the wire format
// is this layer's business, and the components have no reason to know about it.
const toCamelCase = (key) => key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

const bothNamings = (data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;

  const out = { ...data };
  Object.entries(data).forEach(([key, value]) => {
    const camel = toCamelCase(key);
    if (camel !== key && out[camel] === undefined) out[camel] = value;
  });
  return out;
};

// ============================================
// Auth
// ============================================
export const loginUser = (username, password) =>
  api.post('/v1.0/auth/login', { username, password });

// ============================================
// PR Events
// ============================================
export const getPrEvents = () =>
  api.get('/v1.0/pr-events');

export const getPrEvent = (trackingId) =>
  api.get(`/v1.0/pr-events/${trackingId}`);

export const createPrEvent = (data) =>
  api.post('/v1.0/pr-events', data);

export const updatePrStatus = (trackingId, status) =>
  api.patch(`/v1.0/pr-events/${trackingId}/status`, { status });

export const getPrTimeline = (trackingId) =>
  api.get(`/v1.0/pr-events/${trackingId}/timeline`);

// ============================================
// Business Transactions
// ============================================
export const getComponents = (options) => api.get('/v1.0/components', options);

export const getBusinessTransactions = (timeFilter, componentName) => {
  if (!componentName) throw new Error('componentName is required');
  return api.get(`/v1.0/business-transactions/filter/${timeFilter}`, { params: { componentName } });
};

export const getBusinessTransactionsByTimeFilter = (timeFilter, componentName, tenantId) => {
  const params = {};
  if (componentName) params.componentName = componentName;
  if (tenantId) params.tenantId = tenantId;
  return api.get(`/v1.0/business-transactions/filter/${timeFilter}`, { params });
};

export const getBusinessTransactionsByDateRange = (from, to, componentName, tenantId) => {
  const params = { from, to };
  if (componentName) params.componentName = componentName;
  if (tenantId) params.tenantId = tenantId;
  return api.get('/v1.0/business-transactions', { params });
};

export const getBusinessTransactionsPaged = (componentName, tenantId, pageToken = null, limit = 25) => {
  const params = { limit };
  if (tenantId) params.tenantId = tenantId;
  if (pageToken) params.pageToken = pageToken;
  return api.get(`/v1.0/business-transactions/component/${encodeURIComponent(componentName)}/paged`, { params });
};

export const getBusinessTransaction = (id) =>
  api.get(`/v1.0/business-transactions/${id}`);

// ============================================
// Alerts
// ============================================
export const getAlerts = () =>
  api.get('/v1.0/alerts');

export const getAlert = (alertId) =>
  api.get(`/v1.0/alerts/${alertId}`);

// ============================================
// Incidents
// ============================================
// Takes request options so a caller can mark an auto-refresh as background —
// see the `background` helper in ./axiosConfig.
export const getIncidents = (options) =>
  api.get('/v1.0/incidents', options);

export const getIncidentsByStatus = (status) =>
  api.get(`/v1.0/incidents/status/${status}`);

export const getIncident = (service, incidentId) =>
  api.get(`/v1.0/incidents/${service}/${incidentId}`);

export const getIncidentHistory = (service, incidentId) =>
  api.get(`/v1.0/incidents/${service}/${incidentId}/history`);

export const updateIncidentStatus = (service, incidentId, data) =>
  api.patch(`/v1.0/incidents/${service}/${incidentId}/status`, bothNamings(data));

export const assignIncident = (service, incidentId, data) =>
  api.patch(`/v1.0/incidents/${service}/${incidentId}/assign`, bothNamings(data));

export const escalateIncident = (service, incidentId, data) =>
  api.post(`/v1.0/incidents/${service}/${incidentId}/escalate`, bothNamings(data));

export const getEscalations = (service, incidentId) =>
  api.get(`/v1.0/incidents/${service}/${incidentId}/escalations`);

export const createIncidentFromAlert = (alertId) =>
  api.post(`/v1.0/incidents/from-alert/${alertId}`);

export const deleteIncident = (service, incidentId) =>
  api.delete(`/v1.0/incidents/${service}/${incidentId}`);

export const updateIncident = (service, incidentId, data) =>
  api.put(`/v1.0/incidents/${service}/${incidentId}`, bothNamings(data));

export const getIncidentNotifications = (service, incidentId) =>
  api.get(`/v1.0/incidents/${service}/${incidentId}/notifications`);

export const getIncidentReview = (service, incidentId) =>
  api.get(`/v1.0/incidents/${service}/${incidentId}/review`);

export const createIncidentReview = (service, incidentId, data) =>
  api.post(`/v1.0/incidents/${service}/${incidentId}/review`, bothNamings(data));

// ============================================
// GitHub Pull Requests — uses the githubApi instance from ./axiosConfig
// ============================================
export const getGithubRepositories = () =>
  githubApi.get('/v1.0/repositories');

export const getGithubDashboard = () =>
  githubApi.get('/v1.0/dashboard');

export const getGithubRepoDashboard = (owner, repo) =>
  githubApi.get(`/v1.0/repositories/${owner}/${repo}/dashboard`);

export const getGithubPullRequests = (owner, repo, state = 'open') =>
  githubApi.get(`/v1.0/repositories/${owner}/${repo}/pull-requests`, { params: { state } });

export const getGithubPullRequest = (owner, repo, prNumber) =>
  githubApi.get(`/v1.0/repositories/${owner}/${repo}/pull-requests/${prNumber}`);

export const getGithubPrReviews = (owner, repo, prNumber) =>
  githubApi.get(`/v1.0/repositories/${owner}/${repo}/pull-requests/${prNumber}/reviews`);

export const getGithubDeployments = (owner, repo) =>
  githubApi.get(`/v1.0/repositories/${owner}/${repo}/deployments`);

export const getGithubRateLimit = () =>
  githubApi.get('/v1.0/rate-limit');

// ============================================
// Tenants
// ============================================
// Reads used by the dashboard's own auto-refresh take request options so that
// refresh can be marked background — see the `background` helper in
// ./axiosConfig and the 401 rule it feeds.
export const getTenants = (options) =>
  api.get('/v1.0/tenants', options);

// ============================================
// Dashboard Overview
// ============================================
export const getDashboardOverview = (fromDate, toDate, options) =>
  api.get('/v1.0/dashboard/overview', { params: { fromDate, toDate }, ...options });

export const getDashboardErrorsOverview = (tenantId, fromDate, toDate) =>
  api.get(`/v1.0/dashboard/${encodeURIComponent(tenantId)}/errors/overview`, { params: { fromDate, toDate } });

export const getDashboardLatencyOverview = (tenantId, fromDate, toDate) =>
  api.get(`/v1.0/dashboard/${encodeURIComponent(tenantId)}/latency/overview`, { params: { fromDate, toDate } });

export const getDashboardErrorsDrilldown = (tenantId, componentName, fromDate, toDate, limit = 50, pageToken = null, options) => {
  const params = { fromDate, toDate, limit };
  if (componentName) params.componentName = componentName;
  if (pageToken) params.pageToken = pageToken;
  return api.get(`/v1.0/dashboard/${encodeURIComponent(tenantId)}/errors/drilldown`, { params, ...options });
};

export const getDashboardLatencyDrilldown = (tenantId, componentName, fromDate, toDate, bucket = null, limit = 50, pageToken = null, options) => {
  const params = { fromDate, toDate, limit };
  if (componentName) params.componentName = componentName;
  if (bucket) params.bucket = bucket;
  if (pageToken) params.pageToken = pageToken;
  return api.get(`/v1.0/dashboard/${encodeURIComponent(tenantId)}/latency/drilldown`, { params, ...options });
};

// ============================================
// Users
// ============================================
export const getUsers = () =>
  api.get('/v1.0/users');

// ============================================
// Finance Metering
// ============================================
export const getFinanceRecords = (params) =>
  api.get('/v1/finance', { params });

// ============================================
// File Operations
// ============================================
export const uploadFiles = (trackingId, formData) =>
  api.post(`/v1.0/file-op/${trackingId}/upload/results`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const deleteFiles = (trackingId) =>
  api.delete(`/v1.0/file-op/${trackingId}/files`);
